import { useState } from "react";
import api, { formatEur, formatHours, formatDate } from "@/lib/api";
import { Upload, Loader2, X, CheckCircle2, Plus, Merge, EyeOff, Pencil } from "lucide-react";

const STATUS_LABEL = { a_demarrer: "À démarrer", en_cours: "En cours", en_attente_client: "Attente client", en_attente_mairie: "Attente mairie", livre: "Livré", abandonne: "Abandonné" };
const KIND_LABEL = {
  concept_franchise: "Concept franchise",
  projet_simple: "Projet simple",
  franchimob: "Franchi'Mob",
  administratif: "Administratif",
  mission_ponctuelle: "Mission ponctuelle",
};

const ACTION_STYLES = {
  create: "bg-emerald-50 text-emerald-800 border-emerald-300",
  merge: "bg-blue-50 text-blue-800 border-blue-300",
  skip: "bg-gray-100 text-gray-500 border-gray-300",
};

export default function ProjectImportModal({ onClose, onDone }) {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null); // {rows, total}
  const [items, setItems] = useState([]); // editable state
  const [err, setErr] = useState("");
  const [committing, setCommitting] = useState(false);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setLoading(true); setErr(""); setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/projects/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      setItems(data.rows.map((r) => ({
        action: r.default_action,
        csv: { ...r.csv },
        project_id: r.matched_existing_id,
        orphan_finance: r.orphan_finance,
        finance_ids: r.orphan_finance.map((f) => f.id),
        matched_existing_nom: r.matched_existing_nom,
        computed: r.computed,
      })));
    } catch (e) {
      setErr(e.response?.data?.detail || "Erreur d'import");
    } finally { setLoading(false); }
  };

  const setItem = (i, patch) => setItems((arr) => arr.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const setCsv = (i, patch) => setItems((arr) => arr.map((x, idx) => idx === i ? { ...x, csv: { ...x.csv, ...patch } } : x));
  const toggleFinance = (i, fid) => setItems((arr) => arr.map((x, idx) => {
    if (idx !== i) return x;
    return { ...x, finance_ids: x.finance_ids.includes(fid) ? x.finance_ids.filter((y) => y !== fid) : [...x.finance_ids, fid] };
  }));

  const commit = async () => {
    setCommitting(true); setErr("");
    try {
      const payload = {
        items: items.map((x) => ({
          action: x.action,
          csv: x.csv,
          project_id: x.project_id,
          orphan_finance: x.orphan_finance,
          finance_ids: x.finance_ids,
        })),
      };
      const { data } = await api.post("/import/projects/confirm", payload);
      onDone(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Erreur de validation");
    } finally { setCommitting(false); }
  };

  const counts = {
    create: items.filter((x) => x.action === "create").length,
    merge: items.filter((x) => x.action === "merge").length,
    skip: items.filter((x) => x.action === "skip").length,
  };
  const financeAttached = items.reduce((s, x) => x.action !== "skip" ? s + x.finance_ids.length : s, 0);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 max-w-6xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="project-import-modal">
        <div className="flex items-center justify-between mb-1">
          <div>
            <h3 className="font-display text-2xl font-bold">Import historique Notion</h3>
            <p className="text-sm text-gray-500">Importez vos projets depuis un export CSV Notion. Choisissez l'action pour chaque ligne.</p>
          </div>
          <button onClick={onClose} data-testid="project-import-close"><X size={18}/></button>
        </div>

        {!preview ? (
          <div className="mt-6 p-8 border-2 border-dashed border-gray-300 rounded-lg text-center">
            <Upload size={32} className="mx-auto text-gray-400 mb-2"/>
            <p className="text-sm text-gray-600 mb-4">Sélectionnez votre export CSV "Projets" depuis Notion</p>
            <label className="inline-flex bg-gray-900 hover:bg-black text-white px-4 py-2 rounded text-sm font-semibold gap-2 cursor-pointer">
              {loading ? <Loader2 size={16} className="animate-spin"/> : <Upload size={16}/>}
              {loading ? "Analyse en cours…" : "Choisir le fichier CSV"}
              <input type="file" accept=".csv" onChange={onPick} className="hidden" disabled={loading} data-testid="project-csv-input"/>
            </label>
            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded mt-4 px-3 py-2">{err}</div>}
          </div>
        ) : (
          <>
            {/* Recap bar */}
            <div className="mt-4 flex items-center gap-3 flex-wrap p-3 rounded bg-gray-50 border border-gray-200 text-sm" data-testid="import-recap">
              <span><span className="font-bold text-emerald-700">{counts.create}</span> à créer</span>
              <span><span className="font-bold text-blue-700">{counts.merge}</span> à fusionner</span>
              <span><span className="font-bold text-gray-500">{counts.skip}</span> ignorés</span>
              <span className="text-gray-400">·</span>
              <span><span className="font-bold">{financeAttached}</span> document(s) à rattacher</span>
              <div className="ml-auto flex gap-2">
                <button data-testid="import-validate-btn" onClick={commit} disabled={committing || (counts.create + counts.merge) === 0} className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
                  {committing ? <Loader2 size={14} className="animate-spin"/> : <CheckCircle2 size={14}/>}
                  Valider les {counts.create + counts.merge} action(s)
                </button>
              </div>
            </div>

            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded mt-3 px-3 py-2">{err}</div>}

            <div className="mt-3 overflow-x-auto" data-testid="import-rows">
              <table className="w-full text-xs">
                <thead className="bg-gray-100 text-gray-600 uppercase tracking-wider">
                  <tr>
                    <th className="px-2 py-2 text-left">Action</th>
                    <th className="px-2 py-2 text-left">Projet</th>
                    <th className="px-2 py-2 text-left">Client</th>
                    <th className="px-2 py-2 text-left">Type / Statut</th>
                    <th className="px-2 py-2 text-right">Montant HT</th>
                    <th className="px-2 py-2 text-right">Temps</th>
                    <th className="px-2 py-2 text-left">Dates</th>
                    <th className="px-2 py-2 text-left">Docs rattachés</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((x, i) => (
                    <tr key={i} className={`border-b border-gray-100 ${x.action === "skip" ? "opacity-50" : ""}`} data-testid={`import-row-${i}`}>
                      <td className="px-2 py-2 align-top">
                        <div className="flex flex-col gap-1">
                          {["create", "merge", "skip"].map((a) => (
                            <label key={a} className={`flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-semibold cursor-pointer ${x.action === a ? ACTION_STYLES[a] : "border-gray-200 text-gray-500"} ${a === "merge" && !x.project_id ? "opacity-40 pointer-events-none" : ""}`}>
                              <input type="radio" data-testid={`import-action-${i}-${a}`} checked={x.action === a} onChange={() => setItem(i, { action: a })} className="hidden"/>
                              {a === "create" ? <><Plus size={10}/> Créer</> : a === "merge" ? <><Merge size={10}/> Fusionner</> : <><EyeOff size={10}/> Ignorer</>}
                            </label>
                          ))}
                          {x.matched_existing_nom && (
                            <div className="text-[10px] text-blue-700">↪ {x.matched_existing_nom}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-2 align-top min-w-[180px]">
                        <input value={x.csv.nom} onChange={(e) => setCsv(i, { nom: e.target.value })} className="border border-gray-200 rounded px-2 py-1 w-full font-semibold"/>
                        {x.csv.prestation && <div className="text-[10px] text-gray-500 mt-0.5">{x.csv.prestation}</div>}
                      </td>
                      <td className="px-2 py-2 align-top">
                        <input value={x.csv.client} onChange={(e) => setCsv(i, { client: e.target.value })} className="border border-gray-200 rounded px-2 py-1 w-full"/>
                      </td>
                      <td className="px-2 py-2 align-top">
                        <select value={x.csv.type_projet} onChange={(e) => setCsv(i, { type_projet: e.target.value })} className="border border-gray-200 rounded px-1 py-0.5 w-full mb-1 text-[10px]">
                          {Object.entries(KIND_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                        <select value={x.csv.status} onChange={(e) => setCsv(i, { status: e.target.value })} className="border border-gray-200 rounded px-1 py-0.5 w-full text-[10px]">
                          {Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-2 align-top text-right">
                        <input type="number" value={x.csv.montant_vendu} onChange={(e) => setCsv(i, { montant_vendu: parseFloat(e.target.value) || 0 })} className="border border-gray-200 rounded px-2 py-1 w-24 font-mono text-right"/>
                        <div className="text-[10px] text-gray-500 mt-0.5">{formatEur(x.csv.montant_vendu)}</div>
                      </td>
                      <td className="px-2 py-2 align-top text-right">
                        <input type="number" step="0.5" value={x.csv.temps_prevu_h} onChange={(e) => setCsv(i, { temps_prevu_h: parseFloat(e.target.value) || 0 })} className="border border-gray-200 rounded px-2 py-1 w-20 font-mono text-right"/>
                        <div className="text-[10px] text-gray-500 mt-0.5">{formatHours(x.csv.temps_prevu_h)}</div>
                      </td>
                      <td className="px-2 py-2 align-top">
                        <input type="date" value={x.csv.date_debut} onChange={(e) => setCsv(i, { date_debut: e.target.value })} className="border border-gray-200 rounded px-1 py-0.5 text-[10px] mb-1"/>
                        <input type="date" value={x.csv.date_objectif} onChange={(e) => setCsv(i, { date_objectif: e.target.value })} className="border border-gray-200 rounded px-1 py-0.5 text-[10px]"/>
                      </td>
                      <td className="px-2 py-2 align-top">
                        {x.orphan_finance.length === 0 ? (
                          <span className="text-gray-400 italic">—</span>
                        ) : (
                          <div className="space-y-1">
                            {x.orphan_finance.map((f) => (
                              <label key={f.id} className="flex items-center gap-1 text-[10px] cursor-pointer">
                                <input type="checkbox" checked={x.finance_ids.includes(f.id)} onChange={() => toggleFinance(i, f.id)} data-testid={`import-fin-${i}-${f.id}`}/>
                                <span className="pill bg-gray-100 text-gray-700">{f.type}</span>
                                <span className="font-mono">{f.numero}</span>
                                <span className="text-gray-500">{formatEur(f.montant_ttc)}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
